import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const Reservation = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7b6bmk9quqsda8uett0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reservation" className="py-16 md:py-24 bg-amber-800">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-8 md:mb-10">
          <p className="text-amber-300 text-xs tracking-[0.3em] uppercase mb-3">{t('res_tag')}</p>
          <h2 className="text-2xl md:text-4xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t('res_title')}
          </h2>
          <p className="text-amber-200/70 text-xs md:text-sm mt-2 md:mt-3">{t('res_subtitle')}</p>
        </div>

        {submitted ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-amber-600 rounded-full">
              <i className="ri-check-line text-white text-2xl" />
            </div>
            <p className="text-white text-lg font-semibold mb-2">{t('res_success_title')}</p>
            <p className="text-amber-200/70 text-sm">{t('res_success_desc')}</p>
          </div>
        ) : (
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white/10 backdrop-blur-sm rounded-lg p-5 md:p-8 flex flex-col gap-3 md:gap-4"
          >
            {/* 수신 이메일 (hidden) */}
            <input type="hidden" name="recipient_email" value="youngbinhotel@hotmail.com" />
            <input type="hidden" name="form_title" value="영빈호텔 실시간 예약 문의" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-amber-200 text-xs mb-1">{t('res_name')} *</label>
                <input
                  name="name"
                  required
                  placeholder={t('res_name_ph')}
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:border-amber-300"
                />
              </div>
              <div>
                <label className="block text-amber-200 text-xs mb-1">{t('res_phone')} *</label>
                <input
                  name="phone"
                  required
                  placeholder={t('res_phone_ph')}
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:border-amber-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-amber-200 text-xs mb-1">{t('res_email')}</label>
              <input
                name="email"
                type="email"
                placeholder="example@email.com"
                className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:border-amber-300"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-amber-200 text-xs mb-1">{t('res_checkin')} *</label>
                <input
                  name="checkin"
                  type="date"
                  required
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-amber-300"
                />
              </div>
              <div>
                <label className="block text-amber-200 text-xs mb-1">{t('res_checkout')} *</label>
                <input
                  name="checkout"
                  type="date"
                  required
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-amber-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-amber-200 text-xs mb-1">{t('res_room')}</label>
                <select
                  name="room_type"
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-amber-300"
                >
                  <option value="Single Room" className="text-gray-800">{t('res_room1')}</option>
                  <option value="Twin Room" className="text-gray-800">{t('res_room2')}</option>
                  <option value="Double Room" className="text-gray-800">{t('res_room3')}</option>
                  <option value="Triple Room" className="text-gray-800">{t('res_room4')}</option>
                  <option value="Ondol Room" className="text-gray-800">{t('res_room5')}</option>
                  <option value="Suite Room" className="text-gray-800">{t('res_room6')}</option>
                </select>
              </div>
              <div>
                <label className="block text-amber-200 text-xs mb-1">인원 수 *</label>
                <select
                  name="guests"
                  required
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-amber-300"
                >
                  <option value="1명" className="text-gray-800">1명</option>
                  <option value="2명" className="text-gray-800">2명</option>
                  <option value="3명" className="text-gray-800">3명</option>
                  <option value="4명 이상" className="text-gray-800">4명 이상</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-amber-200 text-xs mb-1">{t('res_message')}</label>
              <textarea
                name="message"
                rows={3}
                maxLength={500}
                placeholder={t('res_message_ph')}
                className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:border-amber-300 resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {submitting ? t('res_submitting') : t('res_submit')}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default Reservation;
